import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Users, Clock, CalendarDays, Gift, BookOpenCheck, Crown, Check } from "lucide-react";
import { api, fmtDate, euro } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Kicker } from "@/components/Section";
import CourseModal from "@/components/booking/CourseModal";
import TrainingsclubModal from "@/components/trainingsclub/TrainingsclubModal";
import CourseAdvisor from "@/components/booking/CourseAdvisor";
import Calendar from "@/components/booking/Calendar";
import TiltCard from "@/components/TiltCard";
import Reveal from "@/components/Reveal";
import { courseCategories, categoryTestKey } from "@/lib/courseCategories";

const Kurse = () => {
  const { lang, t: cms } = useLang();
  const t = (de, en) => (lang === "de" ? de : en);
  const k = cms.kurse || {};
  const [courses, setCourses] = useState([]);
  const [events, setEvents] = useState([]);
  const [active, setActive] = useState(null); // full course object for modal
  const [preselectSession, setPreselectSession] = useState(null);
  const [autoBook, setAutoBook] = useState(false);
  const [catFilter, setCatFilter] = useState("");
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tcOpen, setTcOpen] = useState(false);
  const [tcInfo, setTcInfo] = useState(null);

  useEffect(() => { api.get("/trainingsclub/info").then((r) => setTcInfo(r.data)).catch(() => {}); }, []);

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/courses"), api.get("/calendar"), api.get("/categories")])
      .then(([c, e, cat]) => { setCourses(c.data); setEvents(e.data); setCats(cat.data.filter((x) => x.name !== "Alle")); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const catsOf = courseCategories;
  const matchesCat = (c) => !catFilter || catsOf(c).includes(catFilter);
  const filtered = courses.filter(matchesCat).sort((a, b) => (a.name || "").localeCompare(b.name || "", "de", { numeric: true, sensitivity: "base" }));

  const openCourse = async (id, sessionId = null, book = false) => {
    const { data } = await api.get(`/courses/${id}`);
    setPreselectSession(sessionId);
    setAutoBook(book);
    setActive(data);
  };

  // Deep-Link: /kurse?course=<id>&session=<id> öffnet direkt das Buchungsfenster
  // /kurse?course=<id>&info=1 öffnet die Detail-/Terminübersicht (statt Buchungsformular)
  // /kurse?open=schnupperkurs öffnet das Popup des passenden Schnupperkurses (robust per Namens-Treffer)
  // /kurse?cat=Anfänger setzt den Kategorie-Filter und scrollt zur Liste
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get("mitgliedschaft") === "1") setTcOpen(true);
    const cid = p.get("course");
    if (cid) openCourse(cid, p.get("session"), p.get("info") !== "1");
    const openKey = p.get("open");
    if (openKey) {
      const k = openKey.toLowerCase();
      api.get("/courses").then(async ({ data }) => {
        const exact = data.find((c) => (c.name || "").toLowerCase().includes(k));
        const match = exact || data.find((c) => {
          const n = (c.name || "").toLowerCase();
          return n.includes("schnupper") || n.includes("play golf");
        });
        if (match) {
          let sid = null;
          const date = p.get("date");
          if (date) {
            try {
              const { data: full } = await api.get(`/courses/${match.id}`);
              const s = (full.sessions || []).find((x) => (x.start || "").startsWith(date));
              sid = s ? s.id : null;
            } catch { sid = null; }
          }
          openCourse(match.id, sid);
        }
      }).catch(() => {});
    }
    const cat = p.get("cat");
    if (cat) {
      setCatFilter(cat === "__trainingsclub__" ? "Trainingsclub" : cat);
      setTimeout(() => document.getElementById("kurse-liste")?.scrollIntoView({ behavior: "smooth", block: "start" }), 500);
    }
  }, []); // eslint-disable-line

  const filteredEvents = events.filter((e) => !catFilter || courseCategories(e).includes(catFilter));

  return (
    <div data-testid="kurse-page">
      <section className="pt-36 md:pt-44 pb-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Kicker>{k.kicker || t("// KURSE & EVENTS · ONLINE BUCHEN", "// COURSES & EVENTS · BOOK ONLINE")}</Kicker>
        <div className="flex flex-row items-start justify-between gap-4 sm:gap-6 mt-4">
          <div className="min-w-0">
            <h1 className="font-heading text-5xl sm:text-6xl md:text-8xl uppercase tracking-tight leading-none text-white fade-up">
              {k.title || t("Kurse", "Courses")}
            </h1>
            <p className="text-neutral-400 text-base md:text-lg max-w-2xl mt-6">{k.intro || t("Wähle Deinen Kurs, sieh freie Termine im Kalender und buche in wenigen Klicks online.", "Choose your course, check available dates in the calendar and book online in a few clicks.")}</p>
          </div>
          <div className="shrink-0">
            <CourseAdvisor lang={lang} variant="square" onBook={(cid, sid) => openCourse(cid, sid, true)} />
          </div>
        </div>

        {/* Category filter */}
        <div id="kurse-liste" className="flex flex-wrap gap-2 mt-8 scroll-mt-32" data-testid="category-filter">
          <button onClick={() => setCatFilter("")} data-testid="cat-filter-alle" className={`font-mono-lab text-xs uppercase tracking-widest px-4 py-2 border ${!catFilter ? "border-[#ff0033] text-[#ff0033]" : "border-white/15 text-neutral-400"}`}>{t("Alle", "All")}</button>
          {cats.map((c) => (
            <button key={c.id} onClick={() => setCatFilter(c.name)} data-testid={`cat-filter-${categoryTestKey(c)}`} className={`font-mono-lab text-xs uppercase tracking-widest px-4 py-2 border ${catFilter === c.name ? "border-[#ff0033] text-[#ff0033]" : "border-white/15 text-neutral-400"}`}>{c.name}</button>
          ))}
        </div>
      </section>

      {/* Trainingsclub-Mitgliedschaft – ganz oben */}
      {tcInfo?.active && (
        <section className="pt-2 pb-6 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="trainingsclub-promo">
          <div className="border border-[#ff0033]/40 bg-gradient-to-br from-[#ff0033]/15 via-[#ff0033]/5 to-transparent p-6 sm:p-10 rounded-lg">
            <div className="flex flex-col lg:flex-row lg:items-center gap-8">
              <div className="flex-1 min-w-0">
                <div className="inline-flex items-center gap-2 border border-[#ff0033]/50 text-[#ff0033] px-3 py-1.5 font-mono-lab text-[10px] uppercase tracking-widest mb-4">
                  <Crown size={13} /> {t("Trainingsclub-Mitgliedschaft", "Training Club Membership")}
                </div>
                <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-white leading-none">{tcInfo.headline}</h2>
                <p className="text-neutral-300 text-sm md:text-base mt-4 max-w-xl">{tcInfo.subline}</p>
                {tcInfo.benefits?.length > 0 && (
                  <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 mt-5 max-w-xl">
                    {tcInfo.benefits.map((b, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm text-neutral-200"><Check size={16} className="text-[#ff0033] mt-0.5 shrink-0" /> {b}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="shrink-0 lg:text-right">
                <div className="flex items-baseline gap-2 lg:justify-end">
                  <span className="font-heading text-5xl text-white">{euro(tcInfo.monthly_price)}</span>
                  <span data-testid="tc-promo-billing-period" className="text-neutral-400 text-sm">/ {t("4 Wochen", "4 weeks")}</span>
                </div>
                <p data-testid="tc-promo-cancellation-notice" className="text-neutral-500 text-xs mt-1">{t("28 Tage · 14 Tage Kündigungsfrist", "28 days · 14 days cancellation notice")}</p>
                <button onClick={() => setTcOpen(true)} data-testid="trainingsclub-join-btn" className="inline-flex items-center gap-2 mt-5 bg-[#ff0033] text-white font-heading text-lg uppercase tracking-wider px-8 py-4 hover:bg-[#cc0029] transition-colors">
                  {t("Mitglied werden", "Become a member")} <ArrowUpRight size={18} />
                </button>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Course tiles */}
      <section className="pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="course-grid">
        {loading ? (
          <p className="text-neutral-500 font-mono-lab text-sm">Lädt…</p>
        ) : filtered.length === 0 ? (
          <p className="text-neutral-500 font-mono-lab text-sm">{k.emptyText || t("Aktuell sind keine Kurse buchbar. Schau bald wieder vorbei.", "No courses available right now. Please check back soon.")}</p>
        ) : (
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {filtered.map((c, i) => (
              <Reveal key={c.id} direction="up" delay={(i % 3) * 0.06} className="h-full">
              <TiltCard max={5} data-testid={`course-card-${c.id}`} className="bg-[#0F0F0F] border border-white/10 hover:border-[#ff0033]/50 transition-colors flex flex-col overflow-hidden h-full">
                <div className="h-1.5" style={{ background: c.color }} />
                <div className="p-4 sm:p-6 flex flex-col flex-1">
                  <span className="font-mono-lab text-[9px] sm:text-[10px] tracking-[0.2em] uppercase" style={{ color: c.color }}>{catsOf(c).join(" · ") || c.category}</span>
                  {c.scheduling_type === "course_group" && (
                    <span className="ml-2 inline-block font-mono-lab text-[8px] sm:text-[9px] tracking-widest uppercase text-[#ff0033] border border-[#ff0033]/50 px-1.5 py-0.5 align-middle" data-testid={`series-badge-${c.id}`}>{t(`Serie · ${c.session_count} Termine`, `Series · ${c.session_count} dates`)}</span>
                  )}
                  <h3 className="font-heading text-lg sm:text-2xl uppercase tracking-tight text-white mt-2 leading-none">{c.name}</h3>
                  <p className="text-neutral-400 text-xs sm:text-sm mt-2 sm:mt-3 leading-relaxed flex-1">{c.short_desc}</p>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-1.5 sm:gap-y-2 gap-x-3 mt-4 sm:mt-5 text-[11px] sm:text-xs">
                    <span className="flex items-center gap-2 text-neutral-300"><CalendarDays size={13} className="text-[#ff0033] shrink-0" /> {c.next_session ? fmtDate(c.next_session, lang) : t("auf Anfrage", "on request")}</span>
                    <span className="flex items-center gap-2 text-neutral-300"><Clock size={13} className="text-[#ff0033] shrink-0" /> {c.duration || "—"}</span>
                    <span className="flex items-center gap-2 text-neutral-300"><Users size={13} className="text-[#ff0033] shrink-0" /> {c.free_spots} {t("frei", "free")}</span>
                    <span className="font-heading text-base sm:text-lg text-white">{c.price > 0 ? euro(c.price) : t("auf Anfrage", "on request")}</span>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-2 mt-4 sm:mt-6">
                    <button onClick={() => openCourse(c.id)} data-testid={`more-${c.id}`} className="flex-1 border border-white/20 text-white font-mono-lab text-[10px] sm:text-[11px] uppercase tracking-widest py-2.5 sm:py-3 hover:border-[#ff0033] hover:text-[#ff0033] transition-colors">{t("Mehr erfahren", "Learn more")}</button>
                    {catsOf(c).includes("VIP") ? (
                      <a href="mailto:training@wolfmichael.de" data-testid={`inquire-${c.id}`} className="flex-1 bg-[#ff0033] text-white font-mono-lab text-[10px] sm:text-[11px] uppercase tracking-widest py-2.5 sm:py-3 hover:bg-[#cc0029] transition-colors flex items-center justify-center gap-1">{t("Fragen Sie uns an", "Inquire with us")} <ArrowUpRight size={13} /></a>
                    ) : (
                      <button onClick={() => openCourse(c.id)} data-testid={`book-${c.id}`} className="flex-1 bg-[#ff0033] text-white font-mono-lab text-[10px] sm:text-[11px] uppercase tracking-widest py-2.5 sm:py-3 hover:bg-[#cc0029] transition-colors flex items-center justify-center gap-1">{t("Jetzt buchen", "Book now")} <ArrowUpRight size={13} /></button>
                    )}
                  </div>
                </div>
              </TiltCard>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* Calendar */}
      <section className="pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Kicker>{k.calendarKicker || t("// JAHRESKALENDER", "// YEAR CALENDAR")}</Kicker>
        <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-white mt-3 mb-6">{k.calendarTitle || t("Termine", "Schedule")}</h2>
        <Calendar events={filteredEvents} lang={lang} initialView="month" onEventClick={(e) => openCourse(e.course_id)} />
      </section>

      {/* Regelkurs */}
      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="border border-[#ff0033]/40 bg-gradient-to-r from-[#ff0033]/10 to-transparent p-6 sm:p-10" data-testid="regelkurs-box">
          <Kicker>{k.regelkursKicker || t("// PLATZREIFE · THEORIE", "// COURSE PERMIT · THEORY")}</Kicker>
          <h2 className="font-heading text-3xl md:text-5xl uppercase tracking-tight text-white mt-3">{k.regelkursTitle || t("Regelkurs & Regel-Tests", "Rules course & tests")}</h2>
          <p className="text-neutral-400 text-sm md:text-base max-w-2xl mt-4 leading-relaxed">
            {k.regelkursText1 || t("Mit diesem Online-Regelkurs möchten wir Ihnen die Golfregeln auf genauso informative wie praxisnahe Weise vermitteln.",
               "With this online rules course we want to teach you the rules of golf in a way that is as informative as it is practical.")}
          </p>
          <p className="text-neutral-400 text-sm md:text-base max-w-2xl mt-3 leading-relaxed">
            {k.regelkursText2 || t("Machen Sie sich stark: Denn nur mit fundierter Regelkunde spielen Sie in Turnieren sicher und können entscheiden, wann und wie Sie straffrei Erleichterung erhalten.",
               "Strengthen your game: only with sound knowledge of the rules can you play tournaments with confidence and decide when and how to take penalty-free relief.")}
          </p>
          <Link to="/regelkurs" data-testid="regelkurs-btn" className="inline-flex items-center gap-3 mt-8 bg-[#ff0033] text-white font-heading text-xl md:text-2xl uppercase tracking-wider px-10 md:px-14 py-4 md:py-5 hover:bg-[#cc0029] transition-colors">
            <BookOpenCheck size={24} /> {k.regelkursCta || t("Zum Regelkurs", "Start rules course")} <ArrowUpRight size={22} />
          </Link>
        </div>
      </section>

      {/* Gutschein-Werbung – ganz unten, eigener Block */}
      <section className="pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Link to="/gutschein" data-testid="gutschein-tile" className="group flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border border-[#ff0033]/40 bg-gradient-to-r from-[#ff0033]/10 to-transparent p-5 sm:p-6 hover:border-[#ff0033] transition-colors">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 shrink-0 bg-[#ff0033] flex items-center justify-center"><Gift className="text-white" size={24} /></div>
            <div>
              <h3 className="font-heading text-xl sm:text-2xl uppercase tracking-tight text-white">{k.voucherTitle || t("Golfkurs-Gutschein verschenken", "Gift a golf voucher")}</h3>
              <p className="text-neutral-400 text-sm mt-1">{k.voucherText || t("Wertgutschein für Kurse & TrackMan · 3 Jahre gültig · sofort als PDF", "Value voucher for courses & TrackMan · valid 3 years · instant PDF")}</p>
            </div>
          </div>
          <span className="font-mono-lab text-xs uppercase tracking-widest text-[#ff0033] flex items-center gap-2 whitespace-nowrap">{k.voucherCta || t("Jetzt verschenken", "Gift now")} <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" /></span>
        </Link>
      </section>

      {active && <CourseModal course={active} lang={lang} preselectSessionId={preselectSession} autoStart={autoBook} onClose={() => { setActive(null); setPreselectSession(null); setAutoBook(false); }} onBooked={load} />}
      {tcOpen && <TrainingsclubModal lang={lang} onClose={() => setTcOpen(false)} />}
    </div>
  );
};

export default Kurse;
